import React, { useCallback, useRef } from "react";

import "easymde/dist/easymde.min.css";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { GeoFieldBase } from "@/src/components/form-fields/GeoField";
import {
  RestrictionPeriodFieldBase,
  toTemporalRestrictionFields,
} from "@/src/components/form-fields/RestrictionPeriodField";

import { CONTENT_MAX_WIDTH_STYLE } from "../../constants/size";
import "../../css/admin/utils/item-status.scss";
import i18n from "../../i18n";
import { getStorefrontUrl } from "../../utils/data/Project";
import ItemStatus from "../../utils/ItemStatus";
import { mapContentsToSmartlistItems } from "../../utils/smartlist";
import { getDefaultSlug } from "../../utils/Translations";
import Block from "../block/Block";
import SelectBox from "../selectBox/SelectBox";
import SmartlistCastingsBlock from "../smartlist/SmartlistCastingsBlock";
import SmartlistTranslations from "../smartlist/SmartlistTranslations";
import ContainerHeader from "../utils/ContainerHeader";
import FormNavigationHeader from "../utils/FormNavigationHeader";

import type { Dispatch, JSX, SetStateAction } from "react";

import type { GeoSelection } from "@/src/components/form-fields/GeoField";
import type { RestrictionPeriodChange } from "@/src/components/form-fields/RestrictionPeriodField";

import type {
  GeoblockMode,
  GetSmartlistResponse,
  SmartlistCasting,
  SmartlistItem,
  UpdatedSmartlist,
} from "../../api/SmartlistAPI.types";
import type { AppState } from "../../reducers";
import type { SelectBoxDisplayItem } from "../selectBox/selectBox.types";
import type { SmartlistTranslationsHandle } from "../smartlist/SmartlistTranslations";

const navigationHeaderLinks = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000097749-create-an-audio-playlist",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000097749-cr%C3%A9er-une-playlist-audio",
};

const tabs = [{ key: "audios", icon: "queue_music", index: 0 }];

interface SmartlistAudioFormProps {
  smartlist: GetSmartlistResponse | UpdatedSmartlist;
  setSmartlist: Dispatch<SetStateAction<UpdatedSmartlist>>;
  onSubmit: (
    updatedSmartlist: UpdatedSmartlist,
    onSuccess?: () => void,
  ) => void;
  isLoading: boolean;
}

export default function SmartlistAudioForm({
  smartlist,
  setSmartlist,
  onSubmit,
  isLoading,
}: SmartlistAudioFormProps): JSX.Element {
  const { t } = useTranslation(["editor-smartlist"]);
  const translationsRef = useRef<SmartlistTranslationsHandle>(null);

  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );
  const projectId = useSelector(
    (state: AppState) => state.architectureReducer.project_uuid,
  );

  const handleLabelChange = useCallback(
    (target: { value: string }) => {
      setSmartlist((currentSmartlist) => ({
        ...currentSmartlist,
        label: target.value,
      }));
    },
    [setSmartlist],
  );

  const handleSmartlistItemsChange = useCallback(
    (contents: (SelectBoxDisplayItem | SmartlistItem)[]): void => {
      setSmartlist((currentSmartlist) => ({
        ...currentSmartlist,
        smartlist_items: mapContentsToSmartlistItems(contents),
      }));
    },
    [setSmartlist],
  );

  const handleGeoblockCountries = useCallback(
    ({ countries, countryGroups }: GeoSelection) => {
      setSmartlist((currentSmartlist) => ({
        ...currentSmartlist,
        countries,
        country_groups: countryGroups,
      }));
    },
    [setSmartlist],
  );

  const handleGeoblockMode = useCallback(
    (mode: GeoblockMode) => {
      setSmartlist((currentSmartlist) => ({
        ...currentSmartlist,
        geoblock_mode: mode,
      }));
    },
    [setSmartlist],
  );

  const handleTemporalRestriction = useCallback(
    (change: RestrictionPeriodChange): void => {
      setSmartlist((currentSmartlist) => ({
        ...currentSmartlist,
        ...toTemporalRestrictionFields(change),
      }));
    },
    [setSmartlist],
  );

  const handleStatusChange = useCallback(
    (enabled: boolean) => {
      setSmartlist((currentSmartlist) => ({
        ...currentSmartlist,
        enabled,
      }));
    },
    [setSmartlist],
  );

  const handleCastingsChange = useCallback(
    (castings: SmartlistCasting[]) => {
      setSmartlist((currentSmartlist) => ({
        ...currentSmartlist,
        castings,
      }));
    },
    [setSmartlist],
  );

  const handleSave = async (): Promise<void> => {
    const { translations, translations_indexes: translationsIndexes } =
      translationsRef.current?.getApiTranslations?.() ?? {
        translations: [],
        translations_indexes: {},
      };

    const isValidForm = (await translationsRef.current?.isValid()) ?? true;

    if (!isValidForm) {
      toast.error(t("form:validationError"));

      return;
    }

    const handleSuccess = (): void => translationsRef.current?.onSaveSuccess();

    onSubmit(
      {
        ...smartlist,
        translations,
        translations_indexes: translationsIndexes,
      } as UpdatedSmartlist,
      handleSuccess,
    );
  };

  const breadcrumbs = [
    { name: t("editor-menu:publishing-management"), url: "/smartlist_audio" },
    { name: t("sectionTitleAudio"), url: "/smartlist_audio" },
    { name: t("edit") },
  ];

  const baseUrl = getStorefrontUrl(projectId, projects);
  const defaultSlug = getDefaultSlug(smartlist.translations);

  return (
    <div
      className="editor-smartlist-form-container editor-has-breadcrumbs mx-auto flex max-w-(--maxWidth) flex-col gap-[0.9375rem]"
      style={CONTENT_MAX_WIDTH_STYLE}
    >
      <FormNavigationHeader
        breadcrumbs={breadcrumbs}
        links={navigationHeaderLinks}
      />
      <ContainerHeader
        withBackBtn
        defaultLabel={t("unamedAudio")}
        item={smartlist}
        itemStatus={ItemStatus.getSmartlistStatus(smartlist)}
        loading={isLoading}
        onChangeLabel={handleLabelChange}
        onChangeState={handleStatusChange}
        onSave={handleSave}
        previewLink={`${baseUrl}/playlist/${defaultSlug ?? smartlist.uuid ?? ""}`}
        type="smartlist"
      />
      <SmartlistTranslations
        ref={translationsRef}
        baseUrl={baseUrl}
        smartlist={smartlist}
      />
      <Block title={t("elements")}>
        <SelectBox
          ignoreSmartlistsAudios
          items={smartlist.smartlist_items}
          label={t("elementsSmartlist")}
          onChangeItems={handleSmartlistItemsChange}
          tabs={tabs}
        />
      </Block>
      <SmartlistCastingsBlock
        onChange={handleCastingsChange}
        value={smartlist.castings ?? []}
      />
      <Block title={t("restrictions")}>
        <div className="flex max-w-[500px] flex-col gap-8">
          <GeoFieldBase
            geoblockMode={smartlist.geoblock_mode}
            onModeChange={handleGeoblockMode}
            onSelectionChange={handleGeoblockCountries}
            selectedCountries={smartlist.countries ?? []}
            selectedCountryGroups={smartlist.country_groups ?? []}
          />
          <RestrictionPeriodFieldBase
            beginDate={smartlist.begin_date}
            checked={Boolean(smartlist.temporal_restriction)}
            endDate={smartlist.end_date}
            label={t("editor-smartlist:availibility")}
            lang={i18n.language}
            onChange={handleTemporalRestriction}
          />
        </div>
      </Block>
    </div>
  );
}
